import { Link, useLocation } from 'react-router'
import { Button, Card, Icon, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { ALL_NAV_ITEMS } from '@/app/navigation/nav-config'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

/** Cheap similarity: how many characters of the path appear in the label. */
const score = (label: string, path: string): number => {
  const needle = path.replace(/[^a-z]/gi, '').toLowerCase()
  const hay = label.replace(/[^a-z]/gi, '').toLowerCase()
  if (!needle) return 0
  let hits = 0
  for (const char of new Set(needle)) if (hay.includes(char)) hits += 1
  return hits / new Set(needle).size
}

export function NotFoundScreen() {
  const { pathname } = useLocation()
  const suggestions = [...ALL_NAV_ITEMS]
    .map((item) => ({ item, value: score(item.label, pathname) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 4)
    .map((entry) => entry.item)

  return (
    <>
      <PageHeader
        title="We can’t find that page"
        subtitle={`Nothing lives at ${pathname}. It may have moved, or the link may be old.`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="support_agent" to="/support">
              Tell support
            </Button>
            <Button variant="primary" size="md" iconStart="home" to="/">
              Back to Home
            </Button>
          </>
        }
      />

      <Card tone="light">
        <Stack direction="row" align="center" gap={3}>
          <Icon name="explore" size={20} />
          <Text variant="caption" weight="semibold">
            Were you looking for one of these?
          </Text>
        </Stack>
        <div className={`${checkout.reasons} pb-mt-2`}>
          {suggestions.map((item) => (
            <Link key={item.to} to={item.to} className={checkout.reason}>
              <Icon name={item.icon} size={20} />
              <Stack grow gap={1}>
                <Text variant="caption" weight="semibold">
                  {item.label}
                </Text>
                <Text variant="eyebrow" tone="secondary">
                  {item.to}
                </Text>
              </Stack>
              <Icon name="chevron_right" size={18} />
            </Link>
          ))}
        </div>
      </Card>
    </>
  )
}

/** Shown when someone hits a bad URL while signed out. */
export function PublicNotFoundScreen() {
  return (
    <Stack
      align="center"
      justify="center"
      gap={5}
      style={{ minHeight: '100dvh', padding: 'var(--pb-space-6)', textAlign: 'center' }}
    >
      <Text as="h1" variant="title" weight="bold">
        We can&rsquo;t find that page
      </Text>
      <Text variant="body" tone="secondary">
        The link may be old, or the page may have moved.
      </Text>
      <Button to="/login" size="lg" iconStart="login">
        Go to log in
      </Button>
    </Stack>
  )
}
